import { Container, Sprite, Texture } from 'pixi.js';
import type { GameWorld } from '../../../world/GameWorld';
import { WORLD_TO_R2 } from '../scale';

const GLOW_WIDTH = 18;
const BODY_WIDTH = 8;
const CORE_WIDTH = 2;
const DASH_COUNT = 14;
const STEP_TICKS = 7;
const DASH_SPEED = 8;
const CYAN = 0x70fff2;
const ACID = 0xc9ff5b;
const WHITE = 0xf4ffff;
const MIN_SEGMENT_LENGTH = 1;

type ProjectilePool = GameWorld['projectiles'];

/**
 * Grade V player beam: a single retained multi-layer visual.
 *
 * The white core is the primary read. Clocked cyan blocks move source→target
 * in discrete steps, while the emitter diamond and target bracket punctuate
 * only the two endpoints. Every Sprite is allocated at scene build time.
 */
export class LinkLaserVisual {
  readonly container = new Container();
  private readonly glow = createBeamSprite();
  private readonly body = createBeamSprite();
  private readonly core = createBeamSprite();
  private readonly emitterHalo = createBeamSprite();
  private readonly emitterCore = createBeamSprite();
  private readonly target = new Container();
  private readonly targetTicks: Sprite[] = [];
  private readonly targetCore = createBeamSprite();
  private readonly dashes: Sprite[] = [];

  constructor() {
    this.container.visible = false;
    this.container.blendMode = 'add';
    this.glow.tint = CYAN;
    this.body.tint = CYAN;
    this.core.tint = WHITE;
    this.emitterHalo.tint = CYAN;
    this.emitterCore.tint = WHITE;
    this.targetCore.tint = WHITE;
    this.emitterHalo.visible = true;
    this.emitterCore.visible = true;
    this.targetCore.visible = true;
    this.container.addChild(this.glow, this.body);
    for (let i = 0; i < DASH_COUNT; i += 1) {
      const dash = createBeamSprite();
      dash.tint = CYAN;
      this.dashes.push(dash);
      this.container.addChild(dash);
    }
    this.container.addChild(this.core, this.emitterHalo, this.emitterCore);
    for (let i = 0; i < 4; i += 1) {
      const tick = createBeamSprite();
      tick.tint = ACID;
      tick.visible = true;
      tick.alpha = 0.9;
      tick.setSize(8, 2);
      tick.position.set(i === 0 ? -11 : i === 1 ? 11 : 0, i === 2 ? -11 : i === 3 ? 11 : 0);
      tick.rotation = i >= 2 ? Math.PI * 0.5 : 0;
      this.targetTicks.push(tick);
      this.target.addChild(tick);
    }
    this.target.addChild(this.targetCore);
    this.container.addChild(this.target);
  }

  hide(): void {
    this.container.visible = false;
  }

  sync(pool: ProjectilePool, slot: number, simTick: number): void {
    const x1 = pool.x[slot]! * WORLD_TO_R2;
    const y1 = pool.y[slot]! * WORLD_TO_R2;
    const x2 = pool.x2[slot]! * WORLD_TO_R2;
    const y2 = pool.y2[slot]! * WORLD_TO_R2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.max(MIN_SEGMENT_LENGTH, Math.hypot(dx, dy));
    const angle = Math.atan2(dy, dx);
    const step = simTick % STEP_TICKS;
    const pulse = step <= 1 ? 1 : 0.68;

    this.container.visible = true;
    placeBeam(this.glow, x1, y1, x2, y2, GLOW_WIDTH);
    this.glow.alpha = 0.1 + pulse * 0.08;
    placeBeam(this.body, x1, y1, x2, y2, BODY_WIDTH);
    this.body.alpha = 0.22 + pulse * 0.12;
    placeBeam(this.core, x1, y1, x2, y2, CORE_WIDTH);
    this.core.alpha = 0.9 + pulse * 0.1;

    const spacing = length / DASH_COUNT;
    const dashLength = Math.max(5, Math.min(16, spacing * 0.42));
    const phase = (simTick * DASH_SPEED) % Math.max(1, spacing);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const accentPhase = Math.floor(simTick / STEP_TICKS);
    for (let i = 0; i < this.dashes.length; i += 1) {
      const dash = this.dashes[i]!;
      const distance = (i * spacing + phase) % length;
      dash.visible = true;
      dash.tint = ((i + accentPhase) & 3) === 0 ? ACID : CYAN;
      dash.alpha = ((i + accentPhase) & 3) === 0 ? 0.92 : 0.58;
      dash.rotation = angle;
      dash.setSize(dashLength, 2);
      dash.position.set(
        Math.round(x1 + cos * distance),
        Math.round(y1 + sin * distance),
      );
    }

    const emitterSize = step <= 1 ? 16 : 12;
    this.emitterHalo.alpha = 0.28 + pulse * 0.18;
    this.emitterHalo.rotation = Math.PI * 0.25;
    this.emitterHalo.setSize(emitterSize, emitterSize);
    this.emitterHalo.position.set(Math.round(x1), Math.round(y1));
    this.emitterCore.alpha = 1;
    this.emitterCore.rotation = Math.PI * 0.25;
    this.emitterCore.setSize(5, 5);
    this.emitterCore.position.set(Math.round(x1), Math.round(y1));

    const targetLocked = Math.trunc(pool.aux0[slot]!) !== 0;
    this.target.visible = targetLocked;
    if (targetLocked) {
      this.target.position.set(Math.round(x2), Math.round(y2));
      this.target.rotation = step <= 1 ? Math.PI * 0.25 : 0;
      this.targetCore.alpha = 1;
      this.targetCore.rotation = Math.PI * 0.25;
      this.targetCore.setSize(step <= 1 ? 7 : 5, step <= 1 ? 7 : 5);
      this.targetCore.position.set(0, 0);
    }
  }

  dispose(): void {
    this.container.destroy({ children: true });
    this.dashes.length = 0;
    this.targetTicks.length = 0;
  }
}

function createBeamSprite(): Sprite {
  const beam = new Sprite(Texture.WHITE);
  beam.anchor.set(0.5);
  beam.roundPixels = true;
  beam.visible = false;
  return beam;
}

function placeBeam(sprite: Sprite, x1: number, y1: number, x2: number, y2: number, width: number): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  sprite.visible = true;
  sprite.rotation = Math.atan2(dy, dx);
  sprite.setSize(Math.max(MIN_SEGMENT_LENGTH, Math.hypot(dx, dy)), width);
  sprite.position.set(Math.round((x1 + x2) * 0.5), Math.round((y1 + y2) * 0.5));
}
